/**
 * TimelineHeaderReportMarkers Component
 *
 * Renders user-submitted report markers in the header.
 * Slots into the sun intensity row (y=30) alongside frost markers.
 * Uses solid vertical lines + dense badges coloured by report type.
 */

import React, { useMemo } from 'react'
import { Typography, useTheme } from '@mui/material'
import { REPORT_LINE_WIDTH, REPORT_MARKER_HEIGHT, REPORT_MARKER_WIDTH, X_AXIS_HEIGHT } from '../../timeline-constants'
import { setHoveredReportIndex, useTimelineContextDispatch } from '../../context/timeline-context'
import { useTimelineStaticContext } from '../../context/timeline-static-context'
import { useTimelineInteractionContext } from '../../context/timeline-interaction-context'
import { TpTooltip } from '~/components'

const HALF_MARKER_WIDTH = REPORT_MARKER_WIDTH / 2

/**
 * TimelineHeaderReportMarkers
 *
 * @param {Object} props
 * @param {Array} props.reportDates - Array of { x, dateString, label, color }
 */
export const TimelineHeaderReportMarkers = () => {
  const { FROST_DATES, REPORT_DATES } = useTimelineStaticContext()
  const { hoveredReportIndex } = useTimelineInteractionContext()
  const dispatch = useTimelineContextDispatch()
  const dates = useMemo(() => [...FROST_DATES, ...REPORT_DATES], [FROST_DATES, REPORT_DATES])

  const theme = useTheme()

  // Render hovered item last so it paints on top in SVG stacking order
  const orderedDates = useMemo(() => {
    if (hoveredReportIndex === null) return dates.map((d, i) => ({ ...d, i }))
    return [
      ...dates.filter((_, i) => i !== hoveredReportIndex).map((d, i) => ({ ...d, i: i < hoveredReportIndex ? i : i + 1 })),
      { ...dates[hoveredReportIndex], i: hoveredReportIndex },
    ]
  }, [dates, hoveredReportIndex])

  if (!dates?.length) return null

  return (
    <>
      {orderedDates.map((date) => (
        <g key={`report-header-${date.i}`} style={{ pointerEvents: 'none' }}>
          {/* Dashed vertical line from badge bottom to header bottom */}
          <line
            x1={date.x}
            y1={30}
            x2={date.x}
            y2={X_AXIS_HEIGHT}
            stroke={date.color}
            strokeWidth={hoveredReportIndex === date.i ? REPORT_LINE_WIDTH * 2 : REPORT_LINE_WIDTH}
            opacity={0.6}
            {...(date.dash ? { strokeDasharray: date.dash } : {})}
          />

          {/* Badge */}
          <TpTooltip
            minWidth={0}
            title={date.label}
            placement="top"
            followCursor={false}
            onOpen={() => dispatch(setHoveredReportIndex({ hoveredReportIndex: date.i }))}
            onClose={() => dispatch(setHoveredReportIndex({ hoveredReportIndex: null }))}
            slotProps={{
              popper: {
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [0, -10],
                    },
                  },
                ],
              },
            }}
          >
            <rect
              x={date.x - HALF_MARKER_WIDTH}
              y={30}
              width={REPORT_MARKER_WIDTH}
              height={REPORT_MARKER_HEIGHT}
              rx={4}
              fill={theme.palette.background.paper}
              stroke={date.color}
              strokeWidth={hoveredReportIndex === date.i ? REPORT_LINE_WIDTH * 2 : REPORT_LINE_WIDTH}
              style={{ pointerEvents: 'all', cursor: 'default' }}
            />
          </TpTooltip>
          <foreignObject x={date.x - HALF_MARKER_WIDTH} y={30} width={REPORT_MARKER_WIDTH} height={REPORT_MARKER_HEIGHT} style={{ pointerEvents: 'none' }}>
            <Typography
              sx={{
                fontSize: '9px',
                fontWeight: 700,
                color: date.color,
                textAlign: 'center',
                lineHeight: `${REPORT_MARKER_HEIGHT}px`,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
            >
              {date.icon} {date.dateString}
            </Typography>
          </foreignObject>
        </g>
      ))}
    </>
  )
}
